import { FC } from "react";

import { Badge, Typography } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { ActionButton } from '@hdi-seguros/web-components.action-button';

import { InsurancePolicy, Car } from "../../../../../../../shared/interfaces";
import { Column, _renderCard, _renderColumns } from "../../../../utils/renderSections";

import { CollapsableSections } from "..";
import { formattedPercentage, formattedPrice } from "../../../../../../../shared/utils";
import { CarAccesories, CarDriver, CarDamage, Question, Endorsement, AseguradoPoliza } from '../../../../../../../shared/interfaces/services';
import { ColSize, RowSize } from "../../../../../../../shared/components";
import { getCirculationCardPDF } from "../../../../utils/circulation-card-document";
import { Possession } from "../../../../../../../shared/interfaces/services/get-consortium-coverage-service";
import { CirculationCardData } from "../../../../../../../shared/interfaces/services/circulation-card-pdf-service";

interface CarPossessionsCollapsableProps {
    possessionInfo: Car;
    insurancePolicyDetail: InsurancePolicy;
    endorsement: Endorsement | undefined;
    insuredsByPolicy: AseguradoPoliza | undefined;
    possessions: Possession[];
}

export const CarPossessionsCollapsable: FC<CarPossessionsCollapsableProps> = ({ possessionInfo, insurancePolicyDetail, endorsement, insuredsByPolicy, possessions }) => {

    const circulationCardData: CirculationCardData = {
        possessionInfo, insurancePolicyDetail, endorsement, insuredsByPolicy, possessions
    }

    const generalData: Array<Column> = [
        {
            label: 'Año',
            value: possessionInfo.anio,
        },
        {
            label: 'Patente',
            value: possessionInfo.patente,
        },
        {
            label: 'Uso',
            value: possessionInfo.uso,
        },
        {
            label: 'Marca',
            value: possessionInfo.marca
        },
        {
            label: 'Modelo',
            value: possessionInfo.modelo,
        },
        {
            label: 'Versión',
            value: possessionInfo.version,
        },
        {
            label: 'GNC',
            value: possessionInfo.gnc,
        },
        {
            label: 'Origen',
            value: possessionInfo.origen,
        },
        {
            label: 'Suma asegurada',
            value: `${ insurancePolicyDetail.moneda }${ formattedPrice(possessionInfo.sumaAsegurada) }`,
        },
        {
            label: 'Tipo',
            value: possessionInfo.tipo,
        },
        {
            label: 'Chasis',
            value: possessionInfo.chasis,
        },
        {
            label: 'Motor',
            value: possessionInfo.motor,
        },
        {
            label: 'Cobertura',
            value: possessionInfo.cobertura,
        },
        {
            label: 'Franquicia',
            value: `${ insurancePolicyDetail.moneda }${ formattedPrice( possessionInfo.importeFranquiciaDanios ) }`,
        },
        {
            label: 'Cláusula ajuste',
            value: formattedPercentage( possessionInfo.clausulaDeAjuste ),
        },
        {
            label: 'Asistencia mecánica',
            value: possessionInfo.asistenciaMecanica,
        },
        {
            label: 'Asistencia técnica',
            value: possessionInfo.asistenciaTecnica,
        },
        {
            label: 'Zona',
            value: possessionInfo.zona,
        },
        {
            label: 'Tarjeta circulación',
            value: possessionInfo.tarjetaCirculacion,
        },
        {
            label: 'Acreedor prendario',
            value: possessionInfo.AcreedorPrendario,
        },
        {
            label: '',
            value: <ActionButton label="Tarjeta de Circulación" type="secondary" className="btn-accent-outline-barbuss" icon={<DownloadOutlined />} onClick={() =>  getCirculationCardPDF(circulationCardData, possessionInfo)}  />,
        },
    ];

    const coverageDescription: Array<Column> = [
        {
            label: 'Cobertura',
            value: possessionInfo.descripcionCobAuto,
            columnSize: { xs: { span: 24 } }
        }
    ];

    const generateBonifications = (): Array<Column> => possessionInfo.bonificacionAuto.map(({ descripcion, porcentaje }) => ({
        label: descripcion,
        value: porcentaje,
    }))

    const generateAccessorie = ({ nombre, valor, esTarifable }: CarAccesories): Array<Column> => ([
        {
            label: 'Detalle',
            value: nombre,
        },
        {
            label: 'Valor',
            value: `${ insurancePolicyDetail.moneda }${ formattedPrice( valor ) }`,
        },
        {
            label: 'Aplica en suma asegurada',
            value: esTarifable,
        },
    ])

    const carAccessories: Array<Column> = [
        {
            label: '',
            value: (
                <ColSize size={100}>
                    {
                        possessionInfo.accesorioAuto.length
                            ? possessionInfo.accesorioAuto.map(( accesorie, key ) => _renderCard( key, generateAccessorie( accesorie )) ) 
                            : <Typography.Text> No hay accesorios </Typography.Text>
                    }
                </ColSize>
            ),
            columnSize: { xs: { span: 24 } }
        }
    ];

    const generateDriver = ({ nombre, apellido, numeroDeRegistro, fechaVencimientoRegistro }: CarDriver ): Array<Column> => ([
        {
            label: 'Nombre',
            value: nombre,
            columnSize: { lg: { span: 6 }, sm: { span: 12 }, xs: { span: 12 } }
        },
        {
            label: 'Apellido',
            value: apellido,
            columnSize: { lg: { span: 6 }, sm: { span: 12 }, xs: { span: 12 } }
        },
        {
            label: 'Número de registro',
            value: numeroDeRegistro,
            columnSize: { lg: { span: 6 }, sm: { span: 12 }, xs: { span: 12 } }
        },
        {
            label: 'Vencimiento del registro',
            value: fechaVencimientoRegistro ? fechaVencimientoRegistro.split('T')[0] : fechaVencimientoRegistro,
            columnSize: { lg: { span: 6 }, sm: { span: 12 }, xs: { span: 12 } }
        }
    ]) 

    const drivers: Array<Column> = [
        {
            label: '',
            value: (
                <ColSize size={100}>
                    {
                        possessionInfo.conductorAuto.length
                            ? possessionInfo.conductorAuto.map(( driver, key ) => _renderCard( key, generateDriver( driver )) ) 
                            : <Typography.Text> No hay conductores </Typography.Text>
                    }
                </ColSize>
            ),
            columnSize: { xs: { span: 24 } }
        }
    ];

    const generateDamage = ({ nombre, estado }: CarDamage): Array<Column> => ([
        {
            label: 'Detalle',
            value: nombre,
            columnSize: { xs: { span: 24 }, sm: { span: 12 } }
        },
        {
            label: 'Estado',
            value: estado,
            columnSize: { xs: { span: 24 }, sm: { span: 12 } }
        },
    ]) 

    const damages: Array<Column> = [
        {
            label: '',
            value: (
                <ColSize size={100}>
                    {
                        possessionInfo.danioAuto.length
                            ? possessionInfo.danioAuto.map(( damage, key ) => _renderCard( key, generateDamage( damage )) ) 
                            : <Typography.Text> No hay daños </Typography.Text>
                    }
                </ColSize>
            ),
            columnSize: { xs: { span: 24 } }
        }
    ];



    const generateAdditionalInfo = ({ descripcionItem, respuesta }: Question): Array<Column> =>  [
        {
            label: 'Item',
            value: descripcionItem,
            columnSize: { xs: { span: 24 }, sm: { span: 12 } }
        },
        {
            label: 'Respuesta',
            value: respuesta,
            columnSize: { xs: { span: 24 }, sm: { span: 12 } }
        },
    ]

    const addittionalInfo: Array<Column> = [
        {
            label: '',
            value: (
                <ColSize size={100}>
                    {
                        possessionInfo.scoring?.pregunta?.length
                            ? possessionInfo.scoring.pregunta.map(( question, key ) => _renderCard( key, generateAdditionalInfo( question )) ) 
                            : <Typography.Text> No hay información adicional </Typography.Text>
                    }
                </ColSize>
            ),
            columnSize: { xs: { span: 24 } }
        }
    ];

    
    
    return (
        <>
            <CollapsableSections 
                title="Datos generales"
                children={  
                    <RowSize rowProps={{gutter:[0 , 10]}} >
                        { _renderColumns( generalData ) }
                    </RowSize> 
                }
            />

            <CollapsableSections 
                title="Coberturas y cláusulas"
                children={
                    <RowSize rowProps={{gutter:[0 , 10]}} >
                        { _renderColumns( coverageDescription ) }
                    </RowSize>
                }
            />

            <CollapsableSections 
                title="Bonificaciones y recargos"
                children={
                    <RowSize rowProps={{gutter:[0 , 10]}} >
                        {
                            possessionInfo.bonificacionAuto.length
                                ?  _renderColumns(generateBonifications()) 
                                : <Typography.Text> No existen bonificaciones </Typography.Text>
                        }
                    </RowSize>
                }
            />

            <CollapsableSections 
                title="Factores multiplicativos"
                children={
                    <>
                        {
                            possessionInfo.factorTarifa.map(({ descripcion }, i) => <RowSize key={ i } > <Badge color="black" text={ descripcion } /> </RowSize> )
                        }   
                    </>
                }
            />

            <CollapsableSections 
                title="Accesorios"
                children={
                    <RowSize rowProps={{gutter:[0 , 10]}}>
                        { _renderColumns( carAccessories ) }
                    </RowSize>
                }
            />

            <CollapsableSections 
                title="Nómina de conductores"
                children={
                    <RowSize rowProps={{gutter:[0 , 10]}} >
                        { _renderColumns( drivers ) }
                    </RowSize>
                }
            />

            <CollapsableSections 
                title="Carta de daños"
                children={
                    <RowSize rowProps={{gutter:[0 , 10]}} >
                        { _renderColumns( damages ) }
                    </RowSize>
                }
            />

            <CollapsableSections 
                title="Información adicional"
                children={
                    <RowSize rowProps={{gutter:[0 , 10]}} >
                        { _renderColumns( addittionalInfo ) }
                    </RowSize>
                }
            />

        </>
    )
}